import type { RefObject } from "react"

import type { IUserModel } from "@/3514/types"
import type { TEmptyAsyncCallback, TEmptyCallback } from "@/shared/types/functions"

import type {
    TCoachingChatCreateDisclaimerAwarenessMutation as TCreateDisclaimerAwarenessMutation,
    TCoachingChatCreateDisclaimerAwarenessMutationOptions as TCreateDisclaimerAwarenessMutationOptions,
    TCoachingChatCreateMatrixFeedbackMutation as TCreateFeedbackMatrixMutation,
    TCoachingChatCreateMatrixFeedbackMutationOptions as TCreateMatrixFeedbackMutationOptions
} from "../api"

import { ECoachingChatMessageAuthor } from "./coaching-chat.api.types"

type TCoachingChatSeedQuestion = {
    questionUuid: string
    question: string
    answer?: string
}

type TCoachingChatInsights = {
    isEnabled: boolean
    previousChatCompletionDate: string
    previousChapterId: number
    summaryCards: Record<string, string>[]
}

enum ECoachingChatEntityMode {
    Full = "Full",
    Light = "Light"
}

type TCoachingChatEntityRef = {
    openAbandonmentModal(onConfirm: TEmptyCallback): void
    areSeedQuestionsCompleted: boolean
}

type TCoachingChatConfigBase = {
    ref: RefObject<TCoachingChatEntityRef>
    id: number
    insights: TCoachingChatInsights
    seedQuestions: TCoachingChatSeedQuestion[]
    availableSummaryCards: Record<string, string> | undefined
    isChatCompleted: boolean
    isChatAvailable: boolean
    areSeedQuestionsRequired: boolean
    chapterId: number
    conversationId: number | undefined
    chatTitle: string
    user: Partial<IUserModel>
}

type TCoachingChatConfig =
    | (TCoachingChatConfigBase & {
          mode: ECoachingChatEntityMode.Light
          onReady: TEmptyCallback
          onSeedQuestionsComplete?: never
          onChatComplete(date: string | null): void
      })
    | (TCoachingChatConfigBase & {
          mode: ECoachingChatEntityMode.Full
          onReady?: never
          onChatComplete: TEmptyAsyncCallback
          onSeedQuestionsComplete(areAllSeedsFilled: boolean): Promise<void>
      })

enum ECoachingChatSeedQuestionFormField {
    Id = "id",
    Index = "index",
    Answer = "answer",
    Question = "question"
}

type TCoachingChatSeedQuestionFormField = {
    id: string
    index: number
    answer: string
    question: string
}

enum ECoachingChatInputFormField {
    ChatInput = "chatInput"
}

type TCoachingChatInputForm = {
    chatInput: string
}

enum ECoachingChatConversationStatus {
    NotAvailable = "notAvailable",
    Locked = "locked",
    ReadyToStart = "readyToStart",
    Connected = "connected", // sockets only
    Created = "created",
    Ready = "ready",
    InProgress = "inProgress",
    Paused = "paused",
    GeneratingSummary = "generatingSummary",
    Completed = "completed"
}

enum ECoachingChatConversationErrorVariant {
    AiStuck = "aiStuck",
    Api = "api",
    Violation = "violation",
    ApiFailure = "apiFailure",
    StreamingFailed = "streamingFailed"
}

enum ECoachingChatFormErrorVariant {
    Api = "api",
    ContentViolation = "contentViolation"
}

type TCoachingChatConversationErrorPayload = {
    messageId?: number
    moderationId?: number
    message?: string
}

type TCoachingChatConversationError = {
    variant: ECoachingChatConversationErrorVariant
    payload?: TCoachingChatConversationErrorPayload
}

type TCoachingChatFormError = {
    variant: ECoachingChatFormErrorVariant
    moderationId?: number
    message?: string
}

enum ECoachingChatSummaryCardMode {
    BuildInChat = "BuildInChat",
    BuildInInsights = "BuildInInsights"
}

type TCoachingChatGuideModalProps = {
    onSubmit: TEmptyAsyncCallback
    mutationHook(options?: TCreateDisclaimerAwarenessMutationOptions): TCreateDisclaimerAwarenessMutation
}

type TCoachingChatMatrixFeedbackModalInputForm = {
    text: string
}

// matrix feedback modal only
type TCoachingChatMatrixFeedbackModalProps = {
    mutationHook(options?: TCreateMatrixFeedbackMutationOptions): TCreateFeedbackMatrixMutation
}

enum ECoachingChatMatrixFeedbackModalFormField {
    Feedback = "text"
}

enum ECoachingChatMatrixFeedbackModalChartQuadrantKey {
    QUADRANT_1 = "QUADRANT_1",
    QUADRANT_2 = "QUADRANT_2",
    QUADRANT_3 = "QUADRANT_3",
    QUADRANT_4 = "QUADRANT_4"
}

type TCoachingChatMatrixFeedbackModalChartGridCell = {
    id: string
    quadrantKey: ECoachingChatMatrixFeedbackModalChartQuadrantKey
    row: number // [0,1,2] inside quadrant
    col: number // [0,1,2] inside quadrant
    left: number
    top: number
    width: number
    height: number
    centerX: number
    centerY: number
    // nested indexes in 6*6 grid (for keyboard navigation)
    globalRow: number // [0,1,2,3,4,5] from top to bottom
    globalCol: number // [0,1,2,3,4,5] from left to right
}

type TCoachingChatMatrixFeedbackModalChartGridRect = {
    left: number
    top: number
    size: number
    centerX: number
    centerY: number
}

type TCoachingChatMatrixFeedbackModalChartDiscreteCoords = { x: number; y: number }

type TCoachingChatMatrixFeedbackModalChartDragSession = {
    startOffsetX: number
    startOffsetY: number
    pointerDownClientX: number
    pointerDownClientY: number
}

type TCoachingChatMatrixFeedbackModalChartDimensionConfig = {
    gridSize: number
    quadrantSize: number
    cellSize: number
    bulletSize: number
}

enum ECoachingChatConversationApiMode {
    Sockets = "sockets",
    Rest = "rest"
}

type TCoachingChatSummaryCard = { title: string; content: string; completedAt?: string }

enum ECoachingChatConversationMessagePhase {
    Waiting = "waiting",
    Done = "done"
}

type TCoachingChatConversationMessage = {
    id: number
    conversationId: number
    content: string
    author: ECoachingChatMessageAuthor
    clientMessageId: string
    isStreamable: boolean
    status: ECoachingChatConversationMessagePhase
}

type TCoachingChatConversation = {
    id: number
    status: ECoachingChatConversationStatus
    messages: TCoachingChatConversationMessage[]
    summary: TCoachingChatSummaryCard[]
    isMatrixFeedbackCompleted: boolean
    completedAt: string | undefined
}

type _TCoachingChatHookSharedParams = {
    entityId: number
    entityMode: ECoachingChatEntityMode
    wasChatCompleted: boolean
}

export {
    ECoachingChatEntityMode,
    ECoachingChatSeedQuestionFormField,
    ECoachingChatInputFormField,
    ECoachingChatConversationStatus,
    ECoachingChatConversationErrorVariant,
    ECoachingChatFormErrorVariant,
    ECoachingChatSummaryCardMode,
    ECoachingChatMatrixFeedbackModalFormField,
    ECoachingChatMatrixFeedbackModalChartQuadrantKey,
    ECoachingChatConversationApiMode,
    ECoachingChatConversationMessagePhase
}
export type {
    TCoachingChatSeedQuestionFormField,
    TCoachingChatInputForm,
    TCoachingChatSeedQuestion,
    TCoachingChatInsights,
    TCoachingChatConfig,
    TCoachingChatEntityRef,
    TCoachingChatConversationErrorPayload,
    TCoachingChatConversationError,
    TCoachingChatFormError,
    TCoachingChatGuideModalProps,
    TCoachingChatMatrixFeedbackModalProps,
    TCoachingChatMatrixFeedbackModalInputForm,
    TCoachingChatMatrixFeedbackModalChartGridCell,
    TCoachingChatMatrixFeedbackModalChartGridRect,
    TCoachingChatMatrixFeedbackModalChartDiscreteCoords,
    TCoachingChatMatrixFeedbackModalChartDragSession,
    TCoachingChatMatrixFeedbackModalChartDimensionConfig,
    TCoachingChatSummaryCard,
    TCoachingChatConversationMessage,
    TCoachingChatConversation,
    _TCoachingChatHookSharedParams
}
